/*eslint-disable*/
import React from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar, Chip, Typography, Box, IconButton, Tooltip, Paper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

const ROLE_CONFIG = {
  COACH:        { label: 'Edifier',     backgroundColor: '#dbeafe', textColor: '#1e40af' },
  TEACHER:      { label: 'Teacher',     backgroundColor: '#d1fae5', textColor: '#065f46' },
  'SITE-LEADER':{ label: 'Site-Leader', backgroundColor: '#fed7aa', textColor: '#9a3412' },
  PRINCIPAL:    { label: 'Principal',   backgroundColor: '#e9d5ff', textColor: '#6b21a8' },
  ADMIN:        { label: 'Admin',       backgroundColor: '#fecaca', textColor: '#991b1b' },
};

const getRoleConfig = (roleType) =>
  ROLE_CONFIG[roleType] || { label: roleType || 'User', backgroundColor: '#f3f4f6', textColor: '#374151' };

const COLUMNS = ['#', 'Image', 'Name', 'Email', 'Role', 'Partnership Site', 'Gender'];

export default function UsersTable({
  tableData,
  userRole,
  setItemToDeleteId,
  setOpen,
  setEditData,
  setEditOpen,
  setIsView,
}) {
  const theme = useTheme();
  const canManage = userRole === 'admin' || userRole === 'site-leader';

  if (!tableData?.length) {
    return (
      <TableContainer component={Paper} sx={{ mt: '33px' }}>
        <Typography variant="h5" sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
          No Records Found
        </Typography>
      </TableContainer>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ mt: '33px' }}>
      <Table sx={{ minWidth: 750 }} aria-label="users table">
        <TableHead>
          <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
            {COLUMNS.map((col) => (
              <TableCell key={col} sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                {col}
              </TableCell>
            ))}
            {canManage && (
              <TableCell align="center" sx={{ fontWeight: 600, minWidth: 140 }}>
                Actions
              </TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {tableData.map((row, index) => {
            if (!row) return null;

            const fullName = `${row.fname || ''} ${row.lname || ''}`.trim()
              || row.email?.split('@')[0]
              || '—';

            // Use totalRoles when available (admin view), fall back to single role
            const roles = row.totalRoles?.length > 0 ? row.totalRoles : [row.role].filter(Boolean);

            const partnershipSite = row.partnershipSite;
            const gender = row.gender || row.info?.gender;

            return (
              <TableRow
                key={row._id || index}
                sx={{ '&:hover': { backgroundColor: theme.palette.action.hover } }}
              >
                {/* # */}
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                  {index + 1}
                </TableCell>

                {/* Image */}
                <TableCell>
                  {row.image ? (
                    <Avatar
                      src={`${row.image}?${row.updatedAt || ''}`}
                      alt={fullName}
                      sx={{ width: 40, height: 40 }}
                    />
                  ) : (
                    <Avatar sx={{ width: 40, height: 40, bgcolor: theme.palette.primary.lighter }}>
                      <AccountCircleIcon sx={{ color: theme.palette.primary.main, width: 36, height: 36 }} />
                    </Avatar>
                  )}
                </TableCell>

                {/* Name */}
                <TableCell>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}
                  >
                    {fullName}
                  </Typography>
                </TableCell>

                {/* Email */}
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {row.email || '—'}
                  </Typography>
                </TableCell>

                {/* Role chip(s) */}
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {roles.length > 0 ? roles.map((r, i) => {
                      const config = getRoleConfig(r);
                      return (
                        <Chip
                          key={i}
                          label={config.label}
                          size="small"
                          sx={{
                            backgroundColor: config.backgroundColor,
                            color: config.textColor,
                            fontWeight: 500,
                            fontSize: '0.72rem',
                            height: 22,
                            borderRadius: 1,
                          }}
                        />
                      );
                    }) : (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                  </Box>
                </TableCell>

                {/* Partnership Site */}
                <TableCell>
                  {partnershipSite ? (
                    <Tooltip title={partnershipSite.name || 'Partnership Site'} arrow placement="top">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={partnershipSite.image || undefined}
                          variant="rounded"
                          sx={{
                            width: 26,
                            height: 26,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'primary.lighter',
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            flexShrink: 0,
                          }}
                        >
                          {!partnershipSite.image &&
                            (partnershipSite.name || 'PS')
                              .trim()
                              .split(/\s+/)
                              .slice(0, 2)
                              .map((w) => w.charAt(0).toUpperCase())
                              .join('')}
                        </Avatar>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {partnershipSite.name || '—'}
                        </Typography>
                      </Box>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.disabled">—</Typography>
                  )}
                </TableCell>

                {/* Gender */}
                <TableCell>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textTransform: 'capitalize' }}
                  >
                    {gender || '—'}
                  </Typography>
                </TableCell>

                {/* Actions */}
                {canManage && (
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <Tooltip title="View" arrow>
                        <IconButton
                          size="small"
                          onClick={() => { setEditData(row); setIsView(true); }}
                          sx={{
                            width: 34, height: 34,
                            bgcolor: 'info.lighter', color: 'info.main',
                            '&:hover': { bgcolor: 'info.light' },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Edit" arrow>
                        <IconButton
                          size="small"
                          onClick={() => { setEditData(row); setEditOpen(true); }}
                          sx={{
                            width: 34, height: 34,
                            bgcolor: 'primary.lighter', color: 'primary.main',
                            '&:hover': { bgcolor: 'primary.light' },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Delete" arrow>
                        <IconButton
                          size="small"
                          onClick={() => { setItemToDeleteId(row._id); setOpen(true); }}
                          sx={{
                            width: 34, height: 34,
                            bgcolor: 'error.lighter', color: 'error.main',
                            '&:hover': { bgcolor: 'error.light' },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                )}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
